// The fixed-step sim loop: advancing the world, reacting to a state change,
// and the rAF frame callback that drives both. Takes the session/renderer as
// parameters rather than closing over module globals, so nothing here is
// process-wide state.
import { LEVELS, SIM_DT, step } from '../sim/index.ts';
import type { World } from '../sim/index.ts';
import { Renderer } from '../render/canvas.ts';
import { nextLevel } from './game.ts';
import { recordScore, saveProgress, unlockLevel } from './progress.ts';
import type { Dom, Session } from './session.ts';
import { releaseWakeLock } from './platform.ts';
import { hideHints, refreshCityChips, refreshPauseToggle, showGameOver, showLevelClear, showRunScore, syncHints, updateHud } from './veils.ts';

export function handleStateTransition(dom: Dom, session: Session): void {
  const world = session.world;
  if (!world || world.state === session.lastWorldState) return;
  if (world.state === 'dead') {
    const prevBest = session.progress.best[world.level.id] ?? 0;
    session.progress = recordScore(session.progress, world.level.id, world.points);
    saveProgress(session.progress);
    showRunScore(dom, world, prevBest);
    session.appState = 'dead';
    // a mid-fade onboarding hint would otherwise sit frozen on screen through
    // the quiet stretch below (nothing drives `syncHints()` once appState
    // leaves 'playing') — cut it immediately so the bloom plays against a
    // calm, empty frame; the next `startLevel()` builds a fresh `Onboarding`
    // regardless, so this has no effect on the tutorial's own state machine
    hideHints(dom, session);
    releaseWakeLock(session);
    refreshPauseToggle(dom, session);
    showGameOver(dom);
  } else if (world.state === 'cleared') {
    session.progress = recordScore(session.progress, world.level.id, world.points);
    const nxt = nextLevel(LEVELS, world.level.id);
    if (nxt) session.progress = unlockLevel(session.progress, nxt.id);
    saveProgress(session.progress);
    refreshCityChips(dom, session);
    session.appState = 'cleared';
    releaseWakeLock(session);
    refreshPauseToggle(dom, session);
    showLevelClear(dom, world);
  }
  session.lastWorldState = world.state;
}

/** Advance the sim by `n` fixed steps, feeding the renderer's cosmetic state each time. */
export function advance(renderer: Renderer, dom: Dom, session: Session, n: number): void {
  const world = session.world;
  if (!world) return;
  for (let i = 0; i < n; i++) {
    if (world.state !== 'playing') break;
    step(world, SIM_DT);
    session.sound.observe(world); // read right after step() — events are cleared at the start of the next one
    renderer.onStep(world);
    session.onboarding?.tick(world);
    if (world.points !== session.hudPoints) updateHud(dom, session, world); // the HUD counter was only ever set at level start
    handleStateTransition(dom, session);
  }
}

/** The radar is part of the live picture: drawn in play, through the crash
 *  bloom, and under the pause veil (so nothing pops when it lifts) — not on
 *  the intro's map preview or behind the clear veil. */
function shouldShowRadar(session: Session): boolean {
  return session.appState === 'playing' || session.appState === 'paused' || session.appState === 'dead';
}

export function renderNow(renderer: Renderer, session: Session): void {
  const world = session.world;
  if (world) renderer.render(world as World, session.dragPlaneId, shouldShowRadar(session));
}

export function frame(renderer: Renderer, dom: Dom, session: Session, t: number): void {
  requestAnimationFrame((next) => frame(renderer, dom, session, next));
  if (document.hidden) { session.last = t; return; }
  const dt = Math.min((t - session.last) / 1000, 0.05);
  session.last = t;
  const world = session.world;
  if (world && session.appState === 'playing' && world.state === 'playing') {
    session.acc += dt;
    let guard = 0;
    while (session.acc >= SIM_DT && guard++ < 10) {
      advance(renderer, dom, session, 1);
      session.acc -= SIM_DT;
      if (!session.world || session.world.state !== 'playing') { session.acc = 0; break; }
    }
    syncHints(dom, session);
  }
  renderNow(renderer, session);
}
